/**
 * O contrato do conector gov.br, do lado da tela.
 *
 * A fonte da verdade e apps/api/src/modulos/govbr/govbr.tipos.ts, e este arquivo
 * e o espelho dela aqui. Os nomes dos campos sao os das colunas de proposito: e
 * assim que a API responde, e reescrever para camelCase criaria uma traducao a
 * mais para manter em dia sem ganhar nada.
 *
 * Uma unica diferenca deliberada em relacao ao servidor: `TentativaGovbr.id`
 * chega como texto e permanece texto. A coluna e `bigserial`
 * (db/migrations/0003_identidade_acesso.sql:216) e nao cabe em `number` com
 * seguranca; `DataTable` tambem exige `id: string` (ui/DataTable.tsx:42). As
 * duas razoes apontam para o mesmo lugar, entao nao ha conversao a fazer.
 *
 * O QUE NAO EXISTE AQUI, E NAO PODE PASSAR A EXISTIR: segredo do cliente. A
 * tabela nao tem coluna para ele e a resposta da API nao o carrega — ele vive no
 * cofre do ambiente e quem o usa e o Keycloak (docs/CONECTOR-GOVBR.md, secao 5).
 * Campo de formulario para segredo nesta tela e defeito, nao funcionalidade.
 */

/**
 * O papel que o conector exige — e ele NAO e o mesmo que a area exige.
 *
 * A area de administracao aceita `ADMIN` e `GESTOR_ADMIN`
 * (nucleo/navegacao/mapa.ts, PAPEIS_ADMINISTRACAO), e a rota protegida so confere
 * essa lista. Ja as duas tabelas do conector so abrem para `ADMIN`
 * (db/migrations/0004_rls_baseline.sql:121-128), e a API repete a exigencia em
 * toda rota do modulo.
 *
 * Sem esta constante, o `GESTOR_ADMIN` atravessaria a rota protegida, receberia
 * 403 da API — ou zero linha, se a guarda um dia saisse do lugar — e a tela lhe
 * mostraria "nao ha nada aqui". Seria mentira: ha, e ele e que nao alcanca.
 * Comparar o papel antes de buscar e o que separa as duas frases.
 */
export const PAPEL_DO_CONECTOR = 'ADMIN';

/**
 * Quem pode ver o painel. Recebe o usuario do `useAuth`, que pode ser nulo
 * enquanto a autenticacao nao concluiu.
 */
export function podeVerConector(usuario: { papeis: readonly string[] } | null): boolean {
  return usuario?.papeis.includes(PAPEL_DO_CONECTOR) ?? false;
}

/** Os tres ambientes parametrizaveis. Semeados pela migracao; a tela edita. */
export const AMBIENTES = ['DSV', 'HML', 'PRD'] as const;
export type Ambiente = (typeof AMBIENTES)[number];

export const MODOS = ['SIMULADO', 'REAL'] as const;
export type Modo = (typeof MODOS)[number];

export const NIVEIS_MINIMOS = ['BRONZE', 'PRATA', 'OURO'] as const;
export type NivelMinimo = (typeof NIVEIS_MINIMOS)[number];

/** Um ambiente, do jeito que o GET /govbr/ambientes devolve. */
export interface AmbienteGovbr {
  ambiente: Ambiente;
  modo: Modo;
  issuer_url: string | null;
  client_id: string | null;
  redirect_uri: string | null;
  alias_idp: string | null;
  escopos: string[];
  nivel_minimo: NivelMinimo;
  ativo: boolean;
  /** ISO 8601 em UTC, ou nulo enquanto ninguem testou a conexao. */
  ultima_verificacao_em: string | null;
  ultima_verificacao_ok: boolean | null;
}

/**
 * O corpo do PUT. E o conjunto inteiro: o metodo substitui a representacao, e
 * campo ausente significa vazio — nao "inalterado". Por isso o formulario envia
 * tudo o que edita, sempre.
 */
export interface DadosAmbiente {
  modo: Modo;
  nivel_minimo: NivelMinimo;
  ativo: boolean;
  escopos: string[];
  issuer_url?: string | null;
  client_id?: string | null;
  redirect_uri?: string | null;
  alias_idp?: string | null;
}

/** Uma linha do historico de tentativas. */
export interface TentativaGovbr {
  /** Texto, e nao numero: `bigserial` nao cabe em `number` com seguranca. */
  id: string;
  ambiente: string;
  etapa: string;
  sucesso: boolean;
  codigo_erro: string | null;
  /** Motivo legivel, em portugues. Nunca detalhe tecnico. */
  mensagem: string | null;
  duracao_ms: number | null;
  /** ISO 8601 em UTC. */
  created_at: string;
}

/** O historico, paginado no servidor. */
export interface PaginaTentativas {
  itens: TentativaGovbr[];
  total: number;
  pagina: number;
  tamanho: number;
}

/** O que o POST de teste devolve. Falha de emissor vem aqui, nao como erro HTTP. */
export interface ResultadoTeste {
  ambiente: Ambiente;
  etapa: string;
  sucesso: boolean;
  codigo_erro: string | null;
  /** Motivo legivel, em portugues, ja pronto para a tela. */
  mensagem: string | null;
  duracao_ms: number;
  /** ISO 8601 em UTC. */
  verificado_em: string;
}

/** Filtro do historico. A paginacao e sempre do servidor. */
export interface FiltroTentativas {
  ambiente?: Ambiente;
  pagina: number;
  tamanho: number;
}

/**
 * Reexportado, não redeclarado: só 25 está entre as opções do seletor
 * "Exibir" (`ui/DataPagination.tsx`, `[10, 25, 50, 100]`) — um valor fora
 * dessa lista deixa o seletor em branco. Mesmo padrão de
 * `fiscalizacao/contrato.ts`.
 */
export { TAMANHO_PAGINA_PADRAO } from '@/ui/DataPagination';

/**
 * Como cada valor fechado se chama na tela.
 *
 * Sao rotulos, nao dado: quem os traduz aqui evita que cada componente invente o
 * seu e a tela fale duas linguas. `humanizeLabel` nao serve — ele devolveria
 * "Simulado" e "Dsv", e o que se quer e o nome que o administrador reconhece.
 */
export const ROTULO_DO_AMBIENTE: Record<Ambiente, string> = {
  DSV: 'Desenvolvimento',
  HML: 'Homologação',
  PRD: 'Produção',
};

export const ROTULO_DO_MODO: Record<Modo, string> = {
  SIMULADO: 'Simulado',
  REAL: 'Real',
};

export const DESCRICAO_DO_MODO: Record<Modo, string> = {
  SIMULADO: 'Fala com o provedor de identidade local, para exercitar o fluxo.',
  REAL: 'Fala com o gov.br, com a credencial cadastrada no cofre do ambiente.',
};

export const ROTULO_DO_NIVEL: Record<NivelMinimo, string> = {
  BRONZE: 'Bronze',
  PRATA: 'Prata',
  OURO: 'Ouro',
};

/**
 * Etapas registraveis em `govbr_tentativas`. A coluna e texto livre no banco e o
 * conjunto so existe em codigo, entao um valor fora da lista e possivel — e por
 * isso `rotuloDaEtapa` devolve o codigo cru em vez de quebrar.
 */
const ROTULO_DA_ETAPA: Record<string, string> = {
  AUTORIZACAO: 'Autorização',
  TOKEN: 'Token',
  USERINFO: 'Dados do usuário',
  VINCULO: 'Vínculo de identidade',
};

export function rotuloDaEtapa(etapa: string): string {
  return ROTULO_DA_ETAPA[etapa] ?? etapa;
}

/**
 * Data e hora em portugues, no fuso de quem le.
 *
 * A API responde sempre em UTC (`ISO 8601`), e o administrador raciocina no
 * horario de Sao Paulo. Converter na tela e o certo: o instante e o mesmo, e
 * quem escolhe a apresentacao e o navegador de quem esta olhando.
 */
export function dataHoraLegivel(iso: string | null): string {
  if (!iso) {
    return '—';
  }

  const instante = new Date(iso);

  if (Number.isNaN(instante.getTime())) {
    return '—';
  }

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(instante);
}

/** Duracao em milissegundos, arredondada para o que cabe numa coluna. */
export function duracaoLegivel(ms: number | null): string {
  if (ms === null || Number.isNaN(ms)) {
    return '—';
  }

  if (ms < 1000) {
    return `${Math.round(ms)} ms`;
  }

  return `${(ms / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} s`;
}

/**
 * Os escopos viajam separados por espaco no pedido de autorizacao, e o servidor
 * recusa item com espaco no meio ou em branco. As duas funcoes abaixo sao o par
 * que o formulario usa para ir e voltar entre a lista e o campo de texto.
 */
export function escoposDoTexto(texto: string): string[] {
  return texto.split(/[\s,]+/).filter((escopo) => escopo.length > 0);
}

export function textoDosEscopos(escopos: string[]): string {
  return escopos.join(' ');
}
